import { useEffect, useState } from "react";
import client from "../lib/api";
import { Link } from "react-router-dom";
import { Clock, Warning, ArrowRight } from "@phosphor-icons/react";

/**
 * Friday-Save Widget.
 *
 * Dashboard countdown that ONLY renders on Fridays and pulses gently to
 * pull the eye. Copy pattern:
 *
 *    "3 PODs still open · finish by 5pm to keep DSO under 21 days"
 *
 * The DSO impact number uses a 3-days-per-missed-POD heuristic (industry
 * average for freight AR — one POD landing Monday instead of Friday
 * pushes the invoice into next week's billing cycle).
 *
 * Auto-hides:
 *   - On non-Fridays
 *   - After 5pm local (nothing to save)
 *   - When the fleet has zero open PODs (they're winning)
 */
export default function FridaySaveWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    client.get("/pod/friday-save-status")
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading || !data) return null;
  if (!data.is_friday) return null;
  if (data.hours_until_5pm <= 0) return null;
  if (data.open_pods_needed <= 0) return null;

  const podCount = data.open_pods_needed;
  const dsoDays = data.dso_impact_days;
  const hoursLeft = data.hours_until_5pm;
  const hoursDisplay = hoursLeft >= 1
    ? `${hoursLeft.toFixed(1)}h`
    : `${Math.round(hoursLeft * 60)}m`;

  // Urgency tier — under 2 hours triggers the alert pulse
  const urgent = hoursLeft <= 2;

  return (
    <div
      data-testid="friday-save-widget"
      className={`rounded-xl border p-4 sm:p-5 relative overflow-hidden ${
        urgent
          ? "border-red-500/40 bg-gradient-to-br from-red-500/[0.10] to-transparent"
          : "border-amber-500/30 bg-gradient-to-br from-amber-500/[0.06] to-transparent"
      }`}
    >
      {/* Pulsing dot — signals urgency without an animation loop we can't kill */}
      <div className="absolute top-3 right-3 flex items-center gap-1.5">
        <span
          className={`h-2 w-2 rounded-full ${urgent ? "bg-red-400" : "bg-amber-400"} animate-pulse`}
        />
        <span className={`text-[10px] uppercase tracking-widest font-bold ${urgent ? "text-red-300" : "text-amber-400"}`}>
          {urgent ? "Last call" : "Friday save"}
        </span>
      </div>

      <div className="flex items-start gap-3">
        <div className={`shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ${
          urgent ? "bg-red-500/20 text-red-300" : "bg-amber-500/20 text-amber-300"
        }`}>
          {urgent ? <Warning size={22} weight="fill" /> : <Clock size={22} weight="fill" />}
        </div>
        <div className="min-w-0 flex-1 pr-24">
          <div className="font-display text-xl sm:text-2xl font-semibold text-white leading-tight">
            <span data-testid="friday-save-pod-count" className="tabular-nums">
              {podCount}
            </span>{" "}
            POD{podCount === 1 ? "" : "s"} still open
          </div>
          <div className="text-sm text-neutral-300 mt-1">
            Finish by <b className="text-white">5pm</b> to save an estimated{" "}
            <span data-testid="friday-save-dso-days" className="text-white font-mono tabular-nums">
              {dsoDays}
            </span>{" "}
            DSO day{dsoDays === 1 ? "" : "s"} of aging.
          </div>
          <div className="text-[11px] text-neutral-500 mt-2 flex items-center gap-3 flex-wrap">
            <span data-testid="friday-save-hours-left" className="tabular-nums">
              {hoursDisplay} until 5pm ({data.timezone})
            </span>
            {data.recent_friday_avg > 0 && (
              <>
                <span className="text-neutral-700">·</span>
                <span>4wk Friday avg: {data.recent_friday_avg.toFixed(1)} PODs</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Link
          to="/app/dispatch"
          data-testid="friday-save-open-dispatch"
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
            urgent
              ? "bg-red-500 hover:bg-red-400 text-black"
              : "bg-amber-500 hover:bg-amber-400 text-black"
          }`}
        >
          Open dispatch <ArrowRight size={12} weight="bold" />
        </Link>
        <span className="text-[11px] text-neutral-500 hidden sm:inline">
          Nudge Friday drivers before EOD
        </span>
      </div>
    </div>
  );
}
